import React, { useState } from 'react';
import { PageSection } from '../../types/page';
import { Button } from '../ui/button';
import { X, ChevronLeft, ChevronRight, ZoomIn, ExternalLink } from 'lucide-react';

interface GalleryItem {
  id: string;
  src: string;
  alt: string;
  title?: string;
  description?: string;
  category?: string;
}

interface GallerySectionContent {
  items: GalleryItem[];
  layout?: 'grid' | 'masonry' | 'carousel';
  columns?: 2 | 3 | 4 | 5;
  showCategories?: boolean;
  enableLightbox?: boolean;
  title?: string;
  backgroundImage?: string;
}

interface GallerySectionProps {
  section: PageSection;
  className?: string;
}

export const GallerySection: React.FC<GallerySectionProps> = ({ 
  section, 
  className = '' 
}) => {
  const content = section.content as GallerySectionContent;
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  if (!content.items || content.items.length === 0) {
    return null;
  }

  const backgroundStyle = content.backgroundImage 
    ? { backgroundImage: `url(${content.backgroundImage})` }
    : {};

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(content.items.map(item => item.category).filter(Boolean)))];
  
  // Filter items by category
  const filteredItems = selectedCategory === 'all' 
    ? content.items 
    : content.items.filter(item => item.category === selectedCategory);

  const layout = content.layout || 'grid';
  const columns = content.columns || 3;

  const gridCols = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5'
  };

  const openLightbox = (index: number) => {
    if (content.enableLightbox !== false) {
      setSelectedImage(index);
    }
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage > 0 ? selectedImage - 1 : filteredItems.length - 1);
    }
  };

  const goToNext = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage < filteredItems.length - 1 ? selectedImage + 1 : 0);
    }
  };

  return (
    <section 
      className={`relative py-20 bg-cover bg-center bg-no-repeat ${className}`}
      style={backgroundStyle}
    >
      {/* Overlay */}
      {content.backgroundImage && (
        <div className="absolute inset-0 bg-black/70" />
      )}
      
      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
            content.backgroundImage ? 'text-white' : 'text-gray-900'
          }`}>
            {section.title || content.title || 'Galería'}
          </h2>
          {section.subtitle && (
            <p className={`text-xl max-w-2xl mx-auto ${
              content.backgroundImage ? 'text-gray-200' : 'text-gray-600'
            }`}>
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Category Filter */}
        {content.showCategories && categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className={content.backgroundImage ? 
                  "border-white text-white hover:bg-white hover:text-gray-900" : 
                  ""
                }
              >
                {category === 'all' ? 'Todas' : category}
              </Button>
            ))}
          </div>
        )}

        {/* Gallery Grid */}
        <div className={`grid ${gridCols[columns]} gap-6`}>
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              className="group relative overflow-hidden rounded-xl bg-gray-100 aspect-square cursor-pointer"
              onClick={() => openLightbox(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openLightbox(index);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Ver imagen: ${item.title || item.alt}`}
            >
              <img
                src={item.src}
                alt={item.alt}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="text-center text-white">
                  <ZoomIn className="h-8 w-8 mx-auto mb-2" />
                  {item.title && (
                    <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  )}
                  {item.description && (
                    <p className="text-sm opacity-90">{item.description}</p>
                  )}
                </div>
              </div>

              {/* Category Badge */}
              {item.category && (
                <div className="absolute top-3 left-3">
                  <span className="bg-black/70 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {item.category}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage !== null && content.enableLightbox !== false && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <X className="h-8 w-8" />
            </button>

            {/* Navigation */}
            {filteredItems.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}

            {/* Image */}
            <div className="max-w-6xl max-h-[90vh] mx-4">
              <img
                src={filteredItems[selectedImage].src}
                alt={filteredItems[selectedImage].alt}
                className="max-w-full max-h-full object-contain"
              />
              
              {/* Image Info */}
              {(filteredItems[selectedImage].title || filteredItems[selectedImage].description) && (
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="bg-black/70 backdrop-blur-sm rounded-lg p-4">
                    {filteredItems[selectedImage].title && (
                      <h3 className="font-semibold text-lg mb-1">
                        {filteredItems[selectedImage].title}
                      </h3>
                    )}
                    {filteredItems[selectedImage].description && (
                      <p className="text-sm opacity-90">
                        {filteredItems[selectedImage].description}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Counter */}
              <div className="absolute top-4 left-4 text-white">
                <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-1">
                  <span className="text-sm">
                    {selectedImage + 1} / {filteredItems.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};